import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BUSINESS, NAV } from "@/lib/site";

const LOGO = "/images/dms-home-improvements-logo.webp";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      data-scrolled={scrolled}
      className={`fixed inset-x-0 top-0 z-50 border-b bg-white transition-[border-color,box-shadow] duration-200 ease-out ${
        scrolled ? "border-line shadow-soft" : "border-transparent"
      }`}
    >
      <div className="container-site flex h-[72px] items-center justify-between gap-6 md:h-20">
        <Link to="/" data-testid="header-logo-link" aria-label="DMS Home Improvements home" className="shrink-0">
          <img
            src={LOGO}
            alt="DMS Home Improvements logo"
            width="150"
            height="53"
            fetchPriority="high"
            className="h-auto w-[132px] md:w-[150px]"
          />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`nav-link-${n.id}`}
              className={({ isActive }) =>
                `text-[15px] font-medium transition-colors duration-200 hover:text-brand ${isActive ? "text-brand" : "text-navy"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-phone-link"
            className="hidden items-center gap-2 font-semibold text-navy transition-colors duration-200 hover:text-brand md:inline-flex"
          >
            <Phone size={18} strokeWidth={2} aria-hidden="true" />
            {BUSINESS.phone}
          </a>
          <Link to="/contact" data-testid="header-quote-button" className="btn btn-brand hidden h-11 px-5 sm:inline-flex">
            Get a Free Quote
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-btn border border-line text-navy transition-colors hover:border-brand hover:text-brand lg:hidden"
          >
            {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-nav"
            data-testid="mobile-nav"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="border-t border-line bg-white lg:hidden"
          >
            <nav aria-label="Mobile" className="container-site flex flex-col py-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.id}
                  to={n.to}
                  data-testid={`mobile-nav-link-${n.id}`}
                  className={({ isActive }) =>
                    `border-b border-line py-3 text-base font-medium ${isActive ? "text-brand" : "text-navy"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a
                href={BUSINESS.phoneHref}
                data-testid="mobile-nav-phone-link"
                className="flex items-center gap-2 py-4 font-semibold text-navy"
              >
                <Phone size={18} aria-hidden="true" />
                Call {BUSINESS.founderFirstName} on {BUSINESS.phone}
              </a>
              <Link to="/contact" data-testid="mobile-nav-quote-button" className="btn btn-brand mb-2 w-full">
                Get a Free Quote
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
